/**
 * BOTANIX ERP — Push Notification Manager
 * File: src/PushNotifications.jsx
 * Import in App.js and add <PushNotificationManager/> inside App()
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_API_URL || 'https://api.botanix.in'}/api`;

// ── UTILITY ───────────────────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
 const padding = '='.repeat((4 - base64String.length % 4) % 4);
 const base64 = (base64String + padding).replace(/-/g,'+').replace(/_/g,'/');
 const rawData = window.atob(base64);
 const outputArray = new Uint8Array(rawData.length);
 for(let i=0; i<rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
 return outputArray;
}

// ── HOOK ──────────────────────────────────────────────────────────────────────
export function usePushNotifications() {
 const [permission, setPermission] = useState(Notification.permission);
 const [subscription, setSubscription] = useState(null);
 const [swRegistration,setSwRegistration]= useState(null);
 const [enabled, setEnabled] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 // Register service worker on mount
 useEffect(() => {
 if(!('serviceWorker' in navigator) || !('PushManager' in window)) return;
 navigator.serviceWorker.register('/sw.js')
 .then(reg => { setSwRegistration(reg); })
 .catch(err => setError('Service worker registration failed'));
 }, []);

 const subscribe = useCallback(async () => {
 setLoading(true); setError('');
 try {
 // Request permission
 const perm = await Notification.requestPermission();
 setPermission(perm);
 if(perm !== 'granted') { setError('Notification permission denied'); setLoading(false); return; }

 // Get VAPID key from backend
 const keyRes = await fetch(`${API}/push/vapid-public-key`);
 const keyData = await keyRes.json();

 // Subscribe to push
 const reg = swRegistration || await navigator.serviceWorker.ready;
 const sub = await reg.pushManager.subscribe({
 userVisibleOnly: true,
 applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
 });

 // Save to backend
 await fetch(`${API}/push/subscribe`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 subscription: sub.toJSON(),
 userId: localStorage.getItem('botanix_user_id') || 'admin',
 userName: localStorage.getItem('botanix_user_name') || 'Admin',
 }),
 });

 setSubscription(sub);
 setEnabled(true);
 localStorage.setItem('botanix_push_enabled', 'true');
 } catch(err) {
 setError(err.message);
 } finally {
 setLoading(false);
 }
 }, [swRegistration]);

 const unsubscribe = useCallback(async () => {
 if(!subscription) return;
 await fetch(`${API}/push/unsubscribe`, {
 method:'POST', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ endpoint: subscription.endpoint }),
 }).catch(()=>{});
 await subscription.unsubscribe();
 setSubscription(null);
 setEnabled(false);
 localStorage.removeItem('botanix_push_enabled');
 }, [subscription]);

 // Send test notification
 const sendTest = useCallback(async () => {
 await fetch(`${API}/push/send`, {
 method:'POST', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({
 title: ' Test Notification',
 body: 'Push notifications are working! You will receive alerts for new bookings.',
 tag: 'test',
 url: '/',
 }),
 });
 }, []);

 return { permission, subscription, enabled, loading, error, subscribe, unsubscribe, sendTest };
}

// ── NOTIFICATION BELL BUTTON (add to header) ──────────────────────────────────
const C = {
 accent:'#3AAF58', accentLight:'#EAF7EE',
 red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
 text:'#1A2E1E', text3:'#7A9A80',
 border:'#E2E8E2', border2:'#CDD8CD', surface:'#FFFFFF',
};

export function NotificationBell() {
 const { permission, enabled, loading, error, subscribe, unsubscribe, sendTest } = usePushNotifications();
 const [showPanel, setShowPanel] = useState(false);
 const [notifications, setNotifications] = useState([
 {id:1, title:'New Booking BTX-C-2026-1004', body:'ABC Pharma MICE · 120 pax · ₹9,80,000', time:'2 min ago', read:false, type:'booking'},
 {id:2, title:'Payment Received ₹2,50,000', body:'ABC Technologies · NEFT', time:'1 hr ago', read:false, type:'payment'},
 {id:3, title:'Low Stock Alert', body:'Paneer — 40kg remaining', time:'3 hrs ago', read:true, type:'alert'},
 {id:4, title:'New Lead — Instagram', body:'Kapoor Family · Wedding · ₹18L', time:'5 hrs ago', read:true, type:'lead'},
 ]);

 const unread = notifications.filter(n=>!n.read).length;

 const markRead = (id) => setNotifications(p=>p.map(n=>n.id===id?{...n,read:true}:n));
 const markAllRead = () => setNotifications(p=>p.map(n=>({...n,read:true})));

 const typeIcon = (type) => ({booking:'',payment:'',alert:'',lead:''}[type]||'');
 const typeColor = (type) => ({booking:C.accent,payment:'#2563A8',alert:'#D4840F',lead:'#C83333'}[type]||C.text3);

 return (
 <div style={{position:'relative'}}>
 {/* Bell button */}
 <button onClick={()=>setShowPanel(!showPanel)}
 style={{position:'relative',width:38,height:38,borderRadius:10,border:`1px solid ${C.border2}`,background:showPanel?C.accentLight:C.surface,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>
 
 {unread>0&&(
 <div style={{position:'absolute',top:-4,right:-4,width:18,height:18,borderRadius:'50%',background:C.red,color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid #fff'}}>
 {unread}
 </div>
 )}
 </button>

 {/* Panel */}
 {showPanel&&(
 <div style={{position:'absolute',top:46,right:0,width:360,background:C.surface,borderRadius:16,boxShadow:'0 8px 32px rgba(0,0,0,.15)',border:`1px solid ${C.border}`,zIndex:1000,overflow:'hidden'}}>

 {/* Header */}
 <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',background:'#F8FAF8'}}>
 <div style={{fontWeight:700,fontSize:14,color:C.text}}> Notifications {unread>0&&<span style={{fontSize:11,color:C.red,fontWeight:600,marginLeft:4}}>({unread} new)</span>}</div>
 <div style={{display:'flex',gap:8}}>
 {unread>0&&<button onClick={markAllRead} style={{fontSize:11,color:C.text3,background:'none',border:'none',cursor:'pointer'}}>Mark all read</button>}
 <button onClick={()=>setShowPanel(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:C.text3}}></button>
 </div>
 </div>

 {/* Push enable/disable */}
 <div style={{padding:'10px 16px',background:enabled?C.accentLight:'#FEF6E7',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
 <div style={{fontSize:12,color:enabled?C.accent:'#D4840F',fontWeight:600}}>
 {enabled?' Push notifications ON':' Enable push notifications'}
 </div>
 <button onClick={enabled?unsubscribe:subscribe} disabled={loading}
 style={{padding:'4px 12px',borderRadius:99,border:`1px solid ${enabled?C.accent:'#D4840F'}`,background:'transparent',color:enabled?C.accent:'#D4840F',fontSize:11,fontWeight:600,cursor:'pointer'}}>
 {loading?'Loading…':enabled?'Disable':'Enable'}
 </button>
 </div>

 {/* Notification list */}
 <div style={{maxHeight:340,overflowY:'auto'}}>
 {notifications.map(n=>(
 <div key={n.id} onClick={()=>markRead(n.id)}
 style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,cursor:'pointer',background:n.read?C.surface:'#F0F8F0',display:'flex',gap:10,alignItems:'flex-start'}}>
 <div style={{width:36,height:36,borderRadius:10,background:typeColor(n.type)+'15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
 {typeIcon(n.type)}
 </div>
 <div style={{flex:1}}>
 <div style={{fontSize:13,fontWeight:n.read?500:700,color:C.text,marginBottom:2}}>{n.title}</div>
 <div style={{fontSize:11,color:C.text3,marginBottom:2}}>{n.body}</div>
 <div style={{fontSize:10,color:C.text3}}>{n.time}</div>
 </div>
 {!n.read&&<div style={{width:8,height:8,borderRadius:'50%',background:C.accent,flexShrink:0,marginTop:4}}/>}
 </div>
 ))}
 </div>

 {/* Footer */}
 <div style={{padding:'10px 16px',borderTop:`1px solid ${C.border}`,display:'flex',gap:8}}>
 {enabled&&(
 <button onClick={sendTest}
 style={{flex:1,padding:'7px',borderRadius:9,border:`1px solid ${C.border2}`,background:C.surface,color:C.text3,fontSize:11,cursor:'pointer'}}>
 Send Test
 </button>
 )}
 <button onClick={()=>setNotifications([])}
 style={{flex:1,padding:'7px',borderRadius:9,border:`1px solid ${C.border2}`,background:C.surface,color:C.text3,fontSize:11,cursor:'pointer'}}>
 Clear All
 </button>
 </div>

 {error&&<div style={{padding:'8px 16px',background:C.redBg,fontSize:11,color:C.red}}>{error}</div>}
 </div>
 )}
 </div>
 );
}

// ── MAIN MANAGER (invisible — just registers SW) ───────────────────────────────
export default function PushNotificationManager() {
 useEffect(() => {
 if('serviceWorker' in navigator) {
 navigator.serviceWorker.register('/sw.js').catch(()=>{});
 }
 }, []);
 return null;
}
